import { gameApi, gameApiWithAuth } from '@/helpers/api'
import { detectMobileOrTablet } from '@/helpers/detectDevice'
import { lazyLoadComponent } from '@/helpers/lazyLoadComponent'
import { useLightBoxStore } from '@/store/LightBox'
import { useUserStore } from '@/store/User'
import axios from 'axios'
import { defineStore } from 'pinia'
const userDevice = detectMobileOrTablet() ? 'mobile' : 'web'

const userStore = useUserStore()
const lbStore = useLightBoxStore()
const HelpMessagePopup = lazyLoadComponent({
  comp: () => import('@/components/MarvelMachine/HelpMessagePopup.vue'),
})
const bannedCountries = ['KR', 'JP', 'SG', 'MY', 'HK', 'MO', 'BE', 'SK']

function purchaseApiErrorHandler(error: any) {
  const errorMessagePopupSetting = {
    items: [{ component: HelpMessagePopup }],
    option: { class: 'marvel-machine-helpMessage-lightbox' },
  }
  const errorCode = error.response.headers['x-arena-web-errorcode']
  let componentProp = {}
  // All Other Error
  componentProp = {
    content: 'generalError',
    data1: error.response.data.message ? error.response.data.message : `Error: ${error.response.status}`,
  }
  // Not Enough Marvel
  if (errorCode == 22011) {
    componentProp = { content: 'noMarvel' }
  }
  // When MarvelMachine is not active and User run Spin without Any Marvel.
  if (errorCode == 22019) {
    componentProp = { content: 'noEvent' }
  }
  // 500 Server Error: Hide server error code.
  if (errorCode == 22000) {
    componentProp = { content: 'generalError', data1: 'Server Error' }
  }
  // Bad Parameter: Missing required parameters
  if (errorCode == 22001) {
    componentProp = { content: 'generalError', data1: `Error: Bad Parameter` }
  }
  // All Other Error
  lbStore.openLightBox({
    ...errorMessagePopupSetting,
    componentProp,
  })
}

interface IMarvelMachineEvent {
  enableSpin: boolean | null
  enableSpinPurchase: boolean | null
}
interface IPrizeItem {
  GroupID: string
  imageName: string
  itemID: number
  itemName: string
}
interface IPrizes {
  slotA: IPrizeItem[]
  slotB: IPrizeItem[]
  slotC: IPrizeItem[]
}

interface ISpinPurchaseOption {
  spinItemID: number
  quantity: number
  price: number
  originPrice: number
}
interface IMarvelMachine {
  event: IMarvelMachineEvent
  prizes: IPrizes
  spinPurchaseOption: ISpinPurchaseOption[]
  doubleMePrice: number
}

export interface IUserCoupon {
  receivedDate: string
  couponCode: string
  itemName: string
  itemImageName: string
  itemID: string
  redeemBy: string
  expiredDateTime: string
  expired: boolean
}

export interface IUser {
  spinBalance: number
  hasOldSpinBalance: boolean | null
  seenDurationalSpinReminder: boolean
  couponTotalCount: number
  couponsDataLoading: boolean
  availableYearsIncoupons: (string | number)[]
  coupons: IUserCoupon[]
  couponsMobile: IUserCoupon[]
  nxBalance: number
  isTradeBlocked: boolean
  tradeWillBlocked: boolean
  isCharacterAvailable: boolean | null
  isUserBannedCountry: string | null | boolean
}

export interface IResultItems {
  itemID?: string
  groupID?: string
  couponCode?: string
  itemName?: number
  itemImageName?: string
}

function getDefaultGameStateOverrides() {
  return {
    resultOn: false,
    isSpinRunning: false,
    isReadyToSpin: true,
    doubleResultOn: false,
    resultItems: [],
    resultItemsSpinId: null,
    isDoubleRunning: false,
    resultDoubleItem: {},
  }
}

const mmBaseAPI = '/marvel-machine'
export const useMMStore = defineStore('MarvelMachine', {
  state: () => ({
    marvelMachine: {
      doubleMePrice: 1000,
      event: {
        enableSpin: null,
        enableSpinPurchase: null,
      },
    } as IMarvelMachine,
    user: {
      spinBalance: 0,
      hasOldSpinBalance: null,
      seenDurationalSpinReminder: false,
      couponTotalCount: 0,
      couponsDataLoading: true, // keep this true as initial for Mobile device.
      availableYearsIncoupons: [],
      coupons: [],
      couponsMobile: [],
      nxBalance: 0,
      isTradeBlocked: false,
      tradeWillBlocked: false,
      isCharacterAvailable: null,
      isUserBannedCountry: null,
    } as IUser,
    game: {
      isGameBeingResetted: false,
      isReadyToSpin: true,
      isSpinRunning: false,
      isPurchaseRunning: false,
      revealItemsAnimation: false,
      resultOn: false,
      resultItems: [] as IResultItems[],
      resultItemsSpinId: null as string | null,
      isDoubleRunning: false,
      doubleResultOn: false,
      resultDoubleItem: {} as IResultItems,
      nxBalanceMessageOn: false,
      animationToggle: true,
    },
    breakpoint: {
      isDesktop: false,
      isMobile: false,
      isTablet: false,
    },
  }),
  getters: {
    isGameBlocked(): boolean {
      let isBlocked = false
      if (this.user.isUserBannedCountry || !this.user.isCharacterAvailable || !userStore.isLoggedIn) {
        isBlocked = true
      }
      return isBlocked
    },
    isGameBlockedOrUnabledSpin(): boolean {
      return this.isGameBlocked || !this.marvelMachine.event.enableSpin
    },
  },
  actions: {
    init() {
      this.getMarvelMachineEvent()
      this.getPrizesList()
      this.getSpinPurchaseOption()
      this.getCountry()
    },
    userInfo() {
      this.getUserNxBalance()
      this.getUserSpinBalance()
      this.getUserCharacterInfo()
    },
    // -- About Game
    async getMarvelMachineEvent() {
      const { data } = await gameApi.get<{ event: IMarvelMachineEvent }>(mmBaseAPI)
      // this.marvelMachine = { ...this.marvelMachine, ...{ event: { enableSpinPurchase: false, enableSpin: false } } }
      this.marvelMachine.event = data.event
    },
    async getPrizesList() {
      const { data } = await gameApi.get<{ prizes: IPrizes }>(`${mmBaseAPI}/prizes`)
      this.marvelMachine = { ...this.marvelMachine, prizes: data.prizes }
    },
    async getSpinPurchaseOption() {
      const { data } = await gameApi.get<{ spinItems: ISpinPurchaseOption[] }>(`${mmBaseAPI}/spin-items`)
      this.marvelMachine.spinPurchaseOption = data.spinItems
    },
    // -- User Info
    async getUserNxBalance(cb?: () => void) {
      const { data } = await axios.get<{ totalAmount: number }>(`/api/wallet/v1/balance`)
      this.user.nxBalance = data.totalAmount
      cb?.()
    },
    async getAvailableYearsInCoupons() {
      if (this.user.availableYearsIncoupons.length === 0) {
        const { data } = await gameApiWithAuth.get<{ receivedYears: (string | number)[] }>(
          `${mmBaseAPI}/coupons/received-years`
        )
        this.user.availableYearsIncoupons = ['View All Years', ...data.receivedYears]
      }
    },
    async getUserCoupons(
      //  Add expire date filter option on API for desktop.
      option: {
        pageNumber?: number
        searchedKeyword?: string
        itemsPerLoadMobile?: number
        itemDiplayFilterSelected?: number
        dateReceivedFilterSelected?: number | string
        expireFilter?: string | string
      } = {
        pageNumber: 1,
        itemDiplayFilterSelected: 13,
        itemsPerLoadMobile: 100,
        searchedKeyword: '',
        dateReceivedFilterSelected: '',
        expireFilter: '',
      },
      cb?: () => void
    ) {
      const { pageNumber, searchedKeyword, itemDiplayFilterSelected, itemsPerLoadMobile, dateReceivedFilterSelected } =
        option
      console.log('dateReceivedFilterSelected :', dateReceivedFilterSelected)
      this.user.couponsDataLoading = true
      if (this.breakpoint.isDesktop) {
        // default page_size is 12
        const { data } = await gameApiWithAuth.get<{ coupons: IUserCoupon[]; totalCount: number }>(
          `${mmBaseAPI}/coupons?page_index=${pageNumber}&page_size=${itemDiplayFilterSelected}&item_name=${searchedKeyword}&received_year=${dateReceivedFilterSelected}`
        )
        this.user.coupons = data.coupons
        this.user.couponTotalCount = data.totalCount
      } else {
        if (pageNumber === 1) {
          // Always Reset Array for mobile when items to show is changed to avoid adding duplicated intersectObserver.
          this.user.couponsMobile = []
        }
        const { data } = await gameApiWithAuth.get<{ coupons: IUserCoupon[]; totalCount: number }>(
          `${mmBaseAPI}/coupons?page_index=${pageNumber}&page_size=${itemsPerLoadMobile}&item_name=${searchedKeyword}`
        )
        if (pageNumber === 1) {
          // Update couponsMobile and couponTotalCount
          this.user.couponsMobile = data.coupons
        } else {
          // Append new coupons to couponsMobile and update couponTotalCount
          this.user.couponsMobile = [...this.user.couponsMobile, ...data.coupons]
        }
        this.user.couponTotalCount = data.totalCount
        cb?.()
      }
      this.user.couponsDataLoading = false
    },
    async getUserSpinBalance() {
      const { data } = await gameApiWithAuth.get<{ spinBalance: number; hasOldSpinBalance: boolean }>(
        `${mmBaseAPI}/spin-balance`
      )
      this.user.spinBalance = data.spinBalance
      this.user.hasOldSpinBalance = data.hasOldSpinBalance
    },
    async getUserCharacterInfo() {
      const { data } = await gameApiWithAuth.get<{
        hasCharacters: boolean
        tradeBlocked: boolean
        tradeWillBlocked: boolean
      }>(`/character-info`)
      this.user.isCharacterAvailable = data.hasCharacters
      this.user.isTradeBlocked = data.tradeBlocked
      this.user.tradeWillBlocked = data.tradeWillBlocked
    },
    async getCountry() {
      const { data } = await gameApi.get(`/country`)
      // this.user = { ...this.user, ...{ isUserBannedCountry: 'BE' } }
      // this.user = {
      //   ...this.user,
      //   isUserBannedCountry: bannedCountries.find((country) => country === data.country.toUpperCase()) || false,
      // }
      const isPreEnv = import.meta.env.VITE_APP_ENV === 'pre'
      this.user = {
        ...this.user,
        isUserBannedCountry: isPreEnv ? false : bannedCountries.includes(data.country.toUpperCase()),
      }
    },
    // -- User Action
    async purchaseMarvel(marvelItem: ISpinPurchaseOption) {
      if (this.game.isPurchaseRunning) return
      try {
        this.game.isPurchaseRunning = true
        await gameApiWithAuth.post<void>(`${mmBaseAPI}/purchase`, {
          platformType: userDevice,
          spinItemID: marvelItem.spinItemID,
        })
        this.getUserNxBalance()
        this.getUserSpinBalance()
        this.getUserCharacterInfo() // TradeBlock Update.
        lbStore.closeLightBox()
        setTimeout(() => {
          lbStore.openLightBox({
            items: [{ component: HelpMessagePopup }],
            componentProp: {
              content: 'thankYouforPurchaseMarvels',
            },
            option: { class: 'marvel-machine-helpMessage-lightbox' },
          })
        }, 500)
      } catch (error) {
        purchaseApiErrorHandler(error)
      } finally {
        this.game.isPurchaseRunning = false
      }
    },
    userGameReset() {
      if (this.game.isSpinRunning || this.game.isDoubleRunning) {
        return
      }
      this.game.isGameBeingResetted = true
      setTimeout(() => {
        this.game = {
          ...this.game,
          ...getDefaultGameStateOverrides(),
          isGameBeingResetted: false,
          nxBalanceMessageOn: false,
        }
      }, 150)
    },
    async runSpin() {
      // Reset Game before runSpin again.
      if (this.game.isGameBeingResetted || !this.game.isReadyToSpin) return
      this.game = {
        ...this.game,
        ...getDefaultGameStateOverrides(),
        isSpinRunning: true,
        isReadyToSpin: false,
      }
      setTimeout(() => {
        this.game.nxBalanceMessageOn = false
      }, 300)

      // runSpin
      try {
        const { data } = await gameApiWithAuth.post<{ coupons: IResultItems[]; spinUseID: string }>(
          `${mmBaseAPI}/spin`,
          { platformType: userDevice }
        )
        this.game = { ...this.game, resultItems: data.coupons, resultItemsSpinId: data.spinUseID }
        if (this.game.animationToggle) {
          this.game.revealItemsAnimation = true
          setTimeout(() => {
            this.game = { ...this.game, resultOn: true, revealItemsAnimation: false, isSpinRunning: false }
          }, 6500)
          setTimeout(() => {
            this.game.isReadyToSpin = true
          }, 7200)
        } else {
          setTimeout(() => {
            this.game.revealItemsAnimation = true
          }, 400)
          setTimeout(() => {
            this.game = { ...this.game, resultOn: true, isSpinRunning: false, revealItemsAnimation: false }
          }, 850)
          setTimeout(() => {
            this.game.isReadyToSpin = true
          }, 1850)
        }
      } catch (error: any) {
        purchaseApiErrorHandler(error)
        setTimeout(() => {
          this.game = { ...this.game, isReadyToSpin: true, isSpinRunning: false }
        }, 3000)
      } finally {
        this.getUserSpinBalance()
      }
    },
    async runDoubleSpin() {
      this.game = { ...this.game, nxBalanceMessageOn: false, isDoubleRunning: true, isReadyToSpin: false }
      try {
        const { data } = await gameApiWithAuth.post<{ coupons: IResultItems[] }>(`${mmBaseAPI}/double-spin`, {
          platformType: userDevice,
          spinUseID: this.game.resultItemsSpinId,
        })
        setTimeout(() => {
          this.game = {
            ...this.game,
            resultDoubleItem: data.coupons[0],
            nxBalanceMessageOn: true,
            doubleResultOn: true,
            isDoubleRunning: false,
          }
        }, 700)
        setTimeout(() => {
          this.game = { ...this.game, isReadyToSpin: true }
        }, 1700)
      } catch (error) {
        purchaseApiErrorHandler(error)
        this.game = {
          ...this.game,
          isDoubleRunning: false,
          isReadyToSpin: true,
        }
      } finally {
        this.getUserNxBalance()
      }
    },
    // -- ETC
    setAnimationToggle() {
      if (!this.game.isSpinRunning) this.game.animationToggle = !this.game.animationToggle
    },
  },
})
